/**
 * Tag Manager Service
 * Handles tag creation, parsing, and ID assignment for the tag editor.
 */

import { Injectable } from '@angular/core'
import {
  CSS_CLASSES,
  DOM_ELEMENT_NAMES,
  HTML_ATTRIBUTES,
  HTML_VALUES,
  TAG_EDITOR_STRINGS,
} from '../tag-editor.constants'
import { InsertedTagCustom } from '../tag-editor.model'

@Injectable({ providedIn: 'root' })
export class TagManagerService {
  /** Generates a unique tag ID using crypto.randomUUID. */
  generateTagId(): string {
    return `${TAG_EDITOR_STRINGS.TAG_PREFIX}${crypto.randomUUID()}`
  }

  /** Creates a styled tag span element with all required data attributes and inline colors. */
  createTagElement(
    id: string,
    label: string,
    content: string | DocumentFragment | Node | null,
    color: string,
    category?: string,
    categoryId?: number,
  ): HTMLElement {
    const span = document.createElement(DOM_ELEMENT_NAMES.SPAN)

    span.setAttribute(HTML_ATTRIBUTES.DATA_TAG_ID, id)
    span.setAttribute(HTML_ATTRIBUTES.ID, id)
    span.setAttribute(HTML_ATTRIBUTES.DATA_LABEL, label)
    span.setAttribute(HTML_ATTRIBUTES.DATA_COLOR, color)
    span.setAttribute(HTML_ATTRIBUTES.CLASS, CSS_CLASSES.TAG_ANCHOR)
    span.setAttribute(HTML_ATTRIBUTES.TITLE, `${HTML_VALUES.TAG_TITLE_PREFIX}${label}`)

    if (category) {
      span.setAttribute(HTML_ATTRIBUTES.DATA_CATEGORY, category)
    }
    if (categoryId !== undefined) {
      span.setAttribute(HTML_ATTRIBUTES.DATA_CATEGORY_ID, categoryId.toString())
    }

    if (content === null) {
      // leave empty
    } else if (content instanceof DocumentFragment) {
      while (content.firstChild) {
        span.appendChild(content.firstChild)
      }
    } else if (content instanceof Node) {
      span.appendChild(content)
    } else {
      span.textContent = content
    }

    const rgb = this.hexToRgb(color)
    if (rgb) {
      span.style.setProperty('--tag-color', `${rgb.r}, ${rgb.g}, ${rgb.b}`)
    }

    return span
  }

  /** Parses all inserted tags from an HTML string, skipping duplicates and nested tags. */
  parseTagsFromContent(html: string): ReadonlyArray<InsertedTagCustom> {
    const container = document.createElement(DOM_ELEMENT_NAMES.DIV)
    container.innerHTML = html

    const tagElements = container.querySelectorAll(
      `${DOM_ELEMENT_NAMES.SPAN}[${HTML_ATTRIBUTES.DATA_TAG_ID}], u[${HTML_ATTRIBUTES.DATA_TAG_ID}]`,
    )

    const tags: InsertedTagCustom[] = []
    const seenIds = new Set<string>()

    tagElements.forEach((element) => {
      const id = element.getAttribute(HTML_ATTRIBUTES.DATA_TAG_ID) || element.getAttribute(HTML_ATTRIBUTES.ID) || ''

      if (!id || seenIds.has(id)) {
        return
      }

      // Skip tags nested inside another tag
      let parent = element.parentElement
      while (parent && parent !== container) {
        if (parent.hasAttribute(HTML_ATTRIBUTES.DATA_TAG_ID)) {
          return
        }
        parent = parent.parentElement
      }

      seenIds.add(id)

      const label = element.getAttribute(HTML_ATTRIBUTES.DATA_LABEL) || ''
      const color = element.getAttribute(HTML_ATTRIBUTES.DATA_COLOR) || ''
      const text = element.textContent || ''

      if (id && (label || text)) {
        tags.push({ id, label: label || text, text, color })
      }
    })

    return tags
  }

  /** Reassigns fresh UUIDs to all tags in pasted HTML to prevent duplicate IDs. */
  assignUniqueIdsToTagsInHTML(html: string): string {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')

    const tagElements = doc.querySelectorAll(
      `${DOM_ELEMENT_NAMES.SPAN}[${HTML_ATTRIBUTES.DATA_TAG_ID}], u[${HTML_ATTRIBUTES.DATA_TAG_ID}]`,
    )

    tagElements.forEach((element) => {
      const htmlElement = element as HTMLElement
      const newTagId = this.generateTagId()

      htmlElement.setAttribute(HTML_ATTRIBUTES.DATA_TAG_ID, newTagId)
      htmlElement.setAttribute(HTML_ATTRIBUTES.ID, newTagId)

      const className = htmlElement.getAttribute(HTML_ATTRIBUTES.CLASS) || ''
      if (!className.includes(CSS_CLASSES.TAG_ANCHOR)) {
        htmlElement.setAttribute(HTML_ATTRIBUTES.CLASS, CSS_CLASSES.TAG_ANCHOR)
      }

      htmlElement.setAttribute(HTML_ATTRIBUTES.CONTENTEDITABLE, 'false')
    })

    const tempDiv = document.createElement(DOM_ELEMENT_NAMES.DIV)
    Array.from(doc.body.childNodes).forEach((node) => tempDiv.appendChild(node.cloneNode(true)))

    return tempDiv.innerHTML
  }

  /**
   * Converts content containing legacy-format tags to the current format.
   *
   * Detects tags by [data-tag-id] selector and migrates any that are missing the
   * tag-anchor class (the canonical marker of the current format). Idempotent —
   * already-current tags are passed through unchanged. Safe to call on every writeValue
   * and in pastePreprocess.
   */
  migrateOldContent(html: string): string {
    if (!html) return html

    const container = document.createElement(DOM_ELEMENT_NAMES.DIV)
    container.innerHTML = html

    container.querySelectorAll<HTMLElement>(`[${HTML_ATTRIBUTES.DATA_TAG_ID}]`).forEach((el) => {
      el.removeAttribute(HTML_ATTRIBUTES.CONTENTEDITABLE)
      el.classList.remove('mceNonEditable')

      if (el.classList.contains(CSS_CLASSES.TAG_ANCHOR)) return

      const id = el.getAttribute(HTML_ATTRIBUTES.DATA_TAG_ID)!
      const label = el.getAttribute(HTML_ATTRIBUTES.DATA_LABEL) || el.textContent?.trim() || ''
      const color = this.extractColorHex(el)
      const category = el.getAttribute(HTML_ATTRIBUTES.DATA_CATEGORY) || undefined
      const rawCategoryId = el.getAttribute(HTML_ATTRIBUTES.DATA_CATEGORY_ID)
      const categoryId = rawCategoryId != null ? parseInt(rawCategoryId, 10) : undefined
      const safeCategory = !isNaN(categoryId as number) ? categoryId : undefined

      const fragment = document.createDocumentFragment()
      el.childNodes.forEach((child) => fragment.appendChild(child.cloneNode(true)))

      el.replaceWith(this.createTagElement(id, label, fragment, color, category, safeCategory))
    })

    return container.innerHTML
  }

  /** Parses a 6-digit hex color string into RGB components. */
  hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    const cleanHex = hex.replace('#', '')
    if (cleanHex.length !== 6) return null

    return {
      r: parseInt(cleanHex.substring(0, 2), 16),
      g: parseInt(cleanHex.substring(2, 4), 16),
      b: parseInt(cleanHex.substring(4, 6), 16),
    }
  }

  /**
   * Extracts a 6-digit hex color string from a legacy tag element.
   *
   * Priority: data-color attribute → inline background-color style → primary fallback.
   * Handles #rrggbb, rgb(r,g,b), and rgba(r,g,b,a) style values.
   */
  private extractColorHex(el: HTMLElement): string {
    const dataColor = el.getAttribute(HTML_ATTRIBUTES.DATA_COLOR)
    if (dataColor) return dataColor.replace('#', '')

    const bg = el.style.backgroundColor
    if (!bg) return '3880ff'

    if (bg.startsWith('#')) return bg.replace('#', '')

    const match = bg.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/)
    if (match) {
      return [match[1], match[2], match[3]]
        .map((ch) => parseInt(ch, 10).toString(16).padStart(2, '0'))
        .join('')
    }

    return '3880ff'
  }
}
